/**
 * 실행 노드 아래로 **자식 실행의 노드들을 펼친다** — A 의 실행 이력 안에서 B(그리고 C…)의 노드별 상태·소요·메모리를
 * 들여쓰기로 이어 본다. 표의 행(<tr>)을 돌려주므로 노드 목록 표 안에 그대로 끼운다(열 4개: 노드·상태·소요·메모리).
 *
 * 펼친 행을 누르면 onOpen 으로 그 자식 실행의 그 노드 로그를 연다(실행 상세 창).
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchRunDetailByDag, fmtMemory, fmtTaskRate, fmtTaskRows, memoryTitle, RunDetail, RunLink, stateLabel, stateStyle } from '../../services/executionApi';
import { formatDuration } from '../../utils/formatters';
import type { RunRef } from './RunDetailModal';

/** 너무 깊은 사슬(서로 부르는 워크플로)을 끝없이 펼치지 않는다 */
const MAX_DEPTH = 6;

/** 실행 노드 이름 옆 펼침 단추 */
export const ChildToggle: React.FC<{ open: boolean; onToggle: () => void }> = ({ open, onToggle }) => {
  const { t } = useTranslation();
  return (
    <button type="button" className="mr-1 text-gray-500 hover:text-gray-800 w-3 inline-block"
      onClick={(e) => { e.stopPropagation(); onToggle(); }} title={open ? t('execution:tree.collapseChild') : t('execution:tree.expandChild')}>
      {open ? '▾' : '▸'}
    </button>
  );
};

interface Props {
  link: RunLink;
  depth: number;
  onOpen: (ref: RunRef) => void;
}

const ChildRunRows: React.FC<Props> = ({ link, depth, onOpen }) => {
  const { t } = useTranslation();
  const [detail, setDetail] = useState<RunDetail | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<Record<string, boolean>>({});

  useEffect(() => {
    let alive = true;
    fetchRunDetailByDag(link.dag_id, link.run_id)
      .then((d) => { if (alive) setDetail(d); })
      .catch((e) => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [link.dag_id, link.run_id]);

  const pad = { paddingLeft: `${depth * 14 + 4}px` };
  if (error || !detail) {
    return (
      <tr className="border-b border-gray-50">
        <td colSpan={6} className="py-0.5 text-gray-400" style={pad}>↳ {error || t('common:ui.loading')}</td>
      </tr>
    );
  }
  return (
    <>
      <tr className="border-b border-gray-50 bg-gray-50/60">
        <td colSpan={6} className="py-0.5 text-[0.6875rem] text-gray-500" style={pad}>
          ↳ <span className="font-medium text-gray-700">{link.name || link.dag_id}</span>
          {detail.firstFailure?.message && <span className="ml-2 text-red-600" title={detail.firstFailure.message}>{detail.firstFailure.task_id}: {detail.firstFailure.message.split('\n')[0]}</span>}
        </td>
      </tr>
      {detail.tasks.map((tk) => (
        <React.Fragment key={tk.task_id}>
          <tr className="border-b border-gray-50 cursor-pointer hover:bg-gray-50" title={t('execution:detail.clickForLog')}
            onClick={() => onOpen({ dagId: link.dag_id, runId: link.run_id, name: link.name, taskId: tk.task_id })}>
            <td className="py-0.5 pr-2 text-gray-700 truncate max-w-[280px]" style={pad} title={tk.task_id}>
              {tk.child && depth < MAX_DEPTH
                ? <ChildToggle open={!!open[tk.task_id]} onToggle={() => setOpen((p) => ({ ...p, [tk.task_id]: !p[tk.task_id] }))} />
                : <span className="inline-block w-3 mr-1" />}
              {tk.task_id}
            </td>
            <td className="py-0.5 pr-2"><span className={`px-1.5 rounded ${stateStyle(tk.state)}`}>{stateLabel(tk.state)}</span></td>
            <td className="py-0.5 text-right text-gray-400 tabular-nums">{tk.duration != null ? formatDuration(Number(tk.duration)) : '-'}</td>
            <td className="py-0.5 pl-2 text-right text-gray-400 tabular-nums">{fmtTaskRows(tk)}</td>
            <td className="py-0.5 pl-2 text-right text-gray-400 tabular-nums">{fmtTaskRate(tk)}</td>
            <td className="py-0.5 pl-2 text-right text-gray-400 tabular-nums" title={memoryTitle(tk.memory)}>{fmtMemory(tk.memory)}</td>
          </tr>
          {tk.child && open[tk.task_id] && <ChildRunRows link={tk.child} depth={depth + 1} onOpen={onOpen} />}
        </React.Fragment>
      ))}
    </>
  );
};

export default ChildRunRows;
